import type { Building } from "building-types/building/buildingTypes";
import type { Doc } from "building-types/doc";
import type { RoomHandle } from "building-types/handles/docEntityHandleTypes";
import type { RoomType } from "building-types/rooms/roomTypesEnum";
import { RoomClass } from "building-types/rooms/roomTypesEnum";
import type { StaticRoomAttributes, RoomAttributes } from "building-types/rooms/staticRoomAttributes";
import { docGetTableEntity } from "doc/entity/docGetTableEntity";
import { getRoomGarageCount } from "doc/getGarageCount";
import { defaultRoomAttributes } from "doc/rooms/roomCatalog";

export function staticRoomAttributes(room: { type: RoomType; attributes?: StaticRoomAttributes }): RoomAttributes {
  const roomTypeAttributes = defaultRoomAttributes(room?.type);
  let attributes: RoomAttributes = {
    ...roomTypeAttributes,
    ...room?.attributes,
    // Name, short name, and room type are always taken from the room type.
    name: roomTypeAttributes.name,
    shortName: roomTypeAttributes.shortName,
    roomType: roomTypeAttributes.roomType,
  };

  /*
   * This guard exists as a hotfix, it will be removed along
   * with a migration eliminating the invalid room classes
   * that prompted the need for it.
   */
  if (!Object.values(RoomClass).includes(attributes.roomClass)) {
    attributes = {
      ...attributes,
      roomClass: RoomClass.HOUSE,
    };
  }

  return attributes;
}

export function docGetStaticRoomAttributes(
  doc: Doc,
  handle: RoomHandle,
  building?: Building,
): RoomAttributes | undefined {
  const staticRoom = docGetTableEntity(doc, handle);

  if (!staticRoom) {
    return undefined;
  }

  const room = building?.rooms[handle.id];

  /**
   * Garage count is dynamically determined based upon
   * the operation type associated w/ the doors within
   * a given room's walls
   */
  const garageCount = room && building ? getRoomGarageCount(building, room) : 0;
  const attributes = staticRoomAttributes(staticRoom);

  return {
    ...attributes,
    // Inverted checkbox rows treat "undefined" as false in the UI.
    // Normalize these flags so conflict detection uses the same effective semantics.
    roomTagHiddenInPlan: attributes.roomTagHiddenInPlan ?? false,
    roomSalesLabelHiddenInBrochure: attributes.roomSalesLabelHiddenInBrochure ?? false,
    garageCount,
  };
}
